import { useEffect, useState } from "react";
import { useMutation, useQuery, useReactiveVar } from "@apollo/client";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import { cloneDeep } from "lodash";
import mixpanel from "mixpanel-browser";

import {
  EOrganizationType,
  IAppState,
  IOrganizationParams,
  IPropertyData,
  IUserData,
  IUserParams,
} from "../../interfaces/interfaces";
import { ReactComponent as Settings } from "../../img/settings.svg";
import {
  CREATE_BUILDING,
  EDIT_ORGANIZATION,
  GET_ORGANIZATION_SUMMARY,
  GET_USER,
} from "../../api/gqlQueries";
import Spinner from "../Spinner";
import {
  organizationIdNameVar,
  smallHeaderBackArrow,
  smallHeaderTitle,
  userIdVar,
} from "../apollo/LocalState";
import Breadcrumbs from "../breadcrumbs/Breadcrumbs";
import BaseRegistrationModal from "../modals/BaseRegistrationModal";

import OrganizationForm from "./OrganizationForm";
import BuildingForm from "./BuildingForm";
import OrganizationDetailsBCC from "./OrganizationDetailsBCC";
import OrganizationDetailsProView from "./OrganizationDetailsProView";

interface IOrganizationData {
  organization: {
    numHomes: number;
    buildings: IPropertyData[];
  };
}

const CreateBuildingModal: React.FC<{
  closeModal: () => void;
}> = ({ closeModal }) => {
  const { organizationId } = useReactiveVar(organizationIdNameVar);
  const [createBuilding] = useMutation(CREATE_BUILDING);
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);
    const buildingName = formData.get("propertyName") as string;
    const timeZone = formData.get("propertyTimeZone") as string;
    const addressLine1 = formData.get("propertyAddressLine1") as string;
    const addressLine2 = formData.get("propertyAddressLine2");
    const city = formData.get("propertyCity") as string;
    const state = formData.get("propertyState") as string;
    const zipcode = formData.get("propertyZipCode") as string;
    const homePasscode = formData.get("propertyHomePasscode") as string;
    createBuilding({
      update: (cache, { data }) => {
        if (!data) {
          // No need to update cache if no data returned.
          return;
        }
        const cachedData = cache.readQuery<
          IOrganizationData,
          IOrganizationParams
        >({
          query: GET_ORGANIZATION_SUMMARY,
          variables: { organizationId },
        });
        if (!cachedData) {
          return;
        }
        const updatedData = cloneDeep(cachedData);
        updatedData.organization.buildings.push(data.createBuilding.building);
        cache.writeQuery({
          data: updatedData,
          query: GET_ORGANIZATION_SUMMARY,
          variables: { organizationId },
        });
        closeModal();
      },
      variables: {
        addressLine1,
        addressLine2: addressLine2 ? addressLine2 !== "" : undefined,
        buildingName,
        city,
        homePasscode,
        state,
        timeZone,
        zipcode,
      },
    });
  };
  return (
    <BaseRegistrationModal
      closeModalFn={closeModal}
      headerText="Create Building"
      Image={<Settings className="organization-create-img" />}
    >
      <BuildingForm closeModal={closeModal} handleSubmit={handleSubmit} />
    </BaseRegistrationModal>
  );
};

const EditOrganizationModal: React.FC<{
  newOrganizationValue: string;
  setNewOrganizationValue: React.Dispatch<React.SetStateAction<string>>;
  closeModal: () => void;
}> = ({ closeModal, newOrganizationValue, setNewOrganizationValue }) => {
  const [editOrganization] = useMutation(EDIT_ORGANIZATION);
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);
    const organizationName = formData.get("organization-name") as string;
    editOrganization({
      variables: { organizationName },
    });
    closeModal();
  };
  return (
    <BaseRegistrationModal
      closeModalFn={closeModal}
      headerText="Edit Organization"
      Image={<Settings className="organization-create-img" />}
    >
      <OrganizationForm
        closeModal={closeModal}
        newOrganizationValue={newOrganizationValue}
        setNewOrganizationValue={setNewOrganizationValue}
        handleSubmit={handleSubmit}
      />
    </BaseRegistrationModal>
  );
};

const OrganizationDetails: React.FC = () => {
  const organizationIdName = useReactiveVar(organizationIdNameVar);
  const [dropDownMenuVisible, setDropDownMenuVisible] = useState(false);
  const [showEditOrganization, setShowEditOrganization] = useState(false);
  const [newOrganizationValue, setNewOrganizationValue] = useState(
    organizationIdName.organizationName
  );
  const [showModal, setShowModal] = useState(false);
  let { organizationId } = useParams();
  if (organizationId === undefined) {
    organizationId = "";
  }
  const userId = useReactiveVar(userIdVar);
  const { loading: userLoad, data: userData } = useQuery<
    IUserData,
    IUserParams
  >(GET_USER, {
    variables: { userId },
  });

  useEffect(() => {
    smallHeaderTitle(organizationIdName.organizationName);
    smallHeaderBackArrow(true);
    if (
      organizationIdName.organizationType ===
      EOrganizationType.PRO_VIEW_ORGANIZATION
    ) {
      mixpanel.track("Organization Details", {
        organization_id: organizationIdName.organizationId,
        organization_name: organizationIdName.organizationName,
        organization_type: organizationIdName.organizationType,
        user_id: userId,
      });
    }
    return () => {
      smallHeaderTitle("");
      smallHeaderBackArrow(false);
    };
  }, []);
  if (userLoad) return <Spinner />;
  if (!userData) throw Error("No data was returned");

  const crumbs = [{ name: organizationIdName.organizationName, path: "" }];

  return (
    <div className="dashboard-container">
      {showEditOrganization && (
        <EditOrganizationModal
          newOrganizationValue={newOrganizationValue}
          setNewOrganizationValue={setNewOrganizationValue}
          closeModal={() => {
            setShowEditOrganization(false);
            setDropDownMenuVisible(false);
          }}
        />
      )}
      {showModal && (
        <CreateBuildingModal closeModal={() => setShowModal(false)} />
      )}
      <div className="dashboard-container-inner">
        <Breadcrumbs
          crumbs={crumbs}
          rootCrumb={{ name: "Organizations", path: "/" }}
        />
        {organizationIdName.organizationType ===
          EOrganizationType.BCC_ORGANIZATION && (
          <OrganizationDetailsBCC
            userData={userData}
            setShowEditOrganization={setShowEditOrganization}
            setShowModal={setShowModal}
            dropDownMenuVisible={dropDownMenuVisible}
            setDropDownMenuVisible={setDropDownMenuVisible}
            organizationId={organizationId}
          />
        )}
        {organizationIdName.organizationType ===
          EOrganizationType.PRO_VIEW_ORGANIZATION && (
          <OrganizationDetailsProView
            userData={userData}
            setShowEditOrganization={setShowEditOrganization}
            dropDownMenuVisible={dropDownMenuVisible}
            setDropDownMenuVisible={setDropDownMenuVisible}
            organizationId={organizationId}
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: IAppState) => {
  return {
    user: state.user,
  };
};

export default connect(mapStateToProps)(OrganizationDetails);
